import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { FormEvent, useId, useMemo, useState } from 'react';
import { RestaurantTrackLocationWithCompanyLocationsCount } from '@durma-soft/gros-sdk/dist/types/restaurants';
import {
  useAuthUserRestaurant,
  useEditRestaurantTrackLocationMutation,
  ValidationError,
} from '@durma-soft/gros-sdk';

import { Input } from '@/components/shared/shadcn-ui/input';
import { Label } from '@/components/shared/shadcn-ui/label';
import { Switch } from '@/components/shared/shadcn-ui/switch';
import { Alert } from '@/components/shared/alert';
import { DefaultSubstituteFoodCombobox } from '@/components/restaurant/track-locations/default-substitute-food-combobox';
import { useChangedObject } from '@/hooks/use-changed-object';
import { Button } from '@/components/shared/shadcn-ui/button';
import { DialogFooter } from '@/components/shared/shadcn-ui/dialog';

import { isNotEmpty } from '@/utils/helpers';

type TrackRestaurantLocationWithoutLocations = Omit<
  RestaurantTrackLocationWithCompanyLocationsCount,
  'company_locations_count'
>;

interface EditRestaurantTrackLocationForm {
  trackLocation: TrackRestaurantLocationWithoutLocations;
  closeModal: () => unknown;
}

interface FormState {
  name: string;
  street: string | null;
  city: string | null;
  zip: string | null;
  state: string | null;
  country: string | null;
  enable_substitute_food_selection: boolean;
  default_substitute_food_id: number | null;
}

export const EditRestaurantTrackLocationForm = ({
  trackLocation,
  closeModal,
}: EditRestaurantTrackLocationForm) => {
  const uid = useId();
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormState>({
    name: trackLocation.name,
    street: trackLocation.street,
    city: trackLocation.city,
    zip: trackLocation.zip,
    state: trackLocation.state,
    country: trackLocation.country,
    enable_substitute_food_selection:
      trackLocation.enable_substitute_food_selection,
    default_substitute_food_id: trackLocation.default_substitute_food_id,
  });

  const restaurant = useAuthUserRestaurant();

  const editRestaurantTrackLocation = useEditRestaurantTrackLocationMutation();

  const [isChanged, changedObj] = useChangedObject(formData, trackLocation);

  const isValid = useMemo(() => {
    return isNotEmpty(formData.name) && isChanged;
  }, [formData, isChanged]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isChanged) return;
    try {
      await editRestaurantTrackLocation.mutateAsync({
        restaurant_id: restaurant.id,
        track_location_id: trackLocation.id,
        ...changedObj,
      });
      closeModal();
      toast.success(t('track-location.edit-success-msg'));
    } catch (error) {
      if (error instanceof ValidationError) {
        return toast.error(error.message);
      }
      toast.error(t('track-location.edit-error-msg'));
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="grid gap-4">
        {/* Name */}
        <div className="flex flex-col gap-2">
          <Label htmlFor={uid + '-name'}>{t('common.name')}</Label>
          <Input
            required
            id={uid + '-name'}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder={t('common.name-placeholder')}
          />
        </div>
        {/* Street */}
        <div className="flex flex-col gap-2">
          <Label htmlFor={uid + '-street'}>
            {t('common.address-details.street')}
          </Label>
          <Input
            id={uid + '-street'}
            value={formData.street || ''}
            onChange={(e) =>
              setFormData({ ...formData, street: e.target.value })
            }
            placeholder={t('common.address-details.street-placeholder')}
          />
        </div>
        {/* City */}
        <div className="flex flex-col gap-2">
          <Label htmlFor={uid + '-city'}>
            {t('common.address-details.city')}
          </Label>
          <Input
            id={uid + '-city'}
            value={formData.city || ''}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            placeholder={t('common.address-details.city-placeholder')}
          />
        </div>
        {/* ZIP */}
        <div className="flex flex-col gap-2">
          <Label htmlFor={uid + '-zip'}>
            {t('common.address-details.zip')}
          </Label>
          <Input
            id={uid + '-zip'}
            value={formData.zip || ''}
            onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
            placeholder={t('common.address-details.zip-placeholder')}
          />
        </div>
        {/* State */}
        <div className="flex flex-col gap-2">
          <Label htmlFor={uid + '-state'}>
            {t('common.address-details.state')}
          </Label>
          <Input
            id={uid + '-state'}
            value={formData.state || ''}
            onChange={(e) =>
              setFormData({ ...formData, state: e.target.value })
            }
            placeholder={t('common.address-details.state-placeholder')}
          />
        </div>
        {/* Country */}
        <div className="flex flex-col gap-2">
          <Label htmlFor={uid + '-country'}>
            {t('common.address-details.country')}
          </Label>
          <Input
            id={uid + '-country'}
            value={formData.country || ''}
            onChange={(e) =>
              setFormData({ ...formData, country: e.target.value })
            }
            placeholder={t('common.address-details.country-placeholder')}
          />
        </div>
        {/* Enable substitute food selection */}
        <div className="flex flex-col gap-2">
          <Label htmlFor={uid + '-enable-substitute-food-selection'}>
            {t('track-location.enable-substitute-food-selection')}
          </Label>
          <Switch
            id={uid + '-enable-substitute-food-selection'}
            checked={formData.enable_substitute_food_selection}
            onCheckedChange={(checked) =>
              setFormData({
                ...formData,
                enable_substitute_food_selection: Boolean(checked),
                default_substitute_food_id: null,
              })
            }
          />
        </div>
        {/* Default substitute food */}
        {!formData.enable_substitute_food_selection && (
          <div className="flex flex-col gap-2">
            <Label htmlFor={uid + '-default-substitute-food'}>
              {t('track-location.default-substitute-food')}
            </Label>
            <DefaultSubstituteFoodCombobox
              id={uid + '-default-substitute-food'}
              restaurantId={restaurant.id}
              value={formData.default_substitute_food_id}
              onChange={(foodId) =>
                setFormData({ ...formData, default_substitute_food_id: foodId })
              }
            />
            {!formData.default_substitute_food_id && (
              <Alert
                variant="destructive"
                title={t('common.warning')}
                description={t(
                  'track-location.no-default-substitute-food-warning',
                )}
              />
            )}
          </div>
        )}
      </div>
      <DialogFooter className="mt-2">
        <Button
          type="submit"
          disabled={!isValid}
          isLoading={editRestaurantTrackLocation.isPending}
        >
          {t('common.save-edit')}
        </Button>
      </DialogFooter>
    </form>
  );
};
